import { css, useTheme } from '@emotion/react';
import * as Dialog from '@radix-ui/react-dialog';
import { jsPDF } from 'jspdf';
import { FileDown, FileText } from 'lucide-react';

import { fontSizes, spacing } from '@/theme/tokens';

export interface ExportSection {
  position: number;
  title: string | null;
  content: string;
  wordCount: number;
  estimatedDurationMs: number | null;
}

export interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  rawContent: string | null;
  sections: ExportSection[];
}

function formatMs(ms: number): string {
  const seconds = Math.round(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function downloadFile(filename: string, content: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ExportDialog({ isOpen, onClose, title, rawContent, sections }: ExportDialogProps) {
  const theme = useTheme();

  const handleTextExport = () => {
    const content = rawContent ?? sections.map((s) => s.content).join('\n---\n');
    downloadFile(`${title}.txt`, content, 'text/plain');
    onClose();
  };

  const handlePdfExport = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let y = margin;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(title, margin, y);
    y += 12;

    let totalWords = 0;
    let totalDurationMs = 0;

    for (const section of sections) {
      if (y > 260) {
        doc.addPage();
        y = margin;
      }

      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(section.title ?? `Section ${section.position + 1}`, margin, y);
      y += 7;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(section.content, maxWidth);
      for (const line of lines) {
        if (y > 280) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, margin, y);
        y += 5;
      }

      y += 3;
      doc.setFontSize(9);
      doc.setTextColor(120);
      const meta = `${section.wordCount} words${section.estimatedDurationMs ? ` · ${formatMs(section.estimatedDurationMs)}` : ''}`;
      doc.text(meta, margin, y);
      doc.setTextColor(0);
      y += 10;

      totalWords += section.wordCount;
      totalDurationMs += section.estimatedDurationMs ?? 0;
    }

    if (y > 270) {
      doc.addPage();
      y = margin;
    }
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${totalWords} words · ${formatMs(totalDurationMs)}`, margin, y);

    doc.save(`${title}.pdf`);
    onClose();
  };

  const buttonStyle = css`
    display: flex;
    align-items: center;
    gap: ${spacing.sm};
    width: 100%;
    padding: ${spacing.md};
    border: 1px solid ${theme.colors.border};
    border-radius: 8px;
    background: transparent;
    color: ${theme.colors.text};
    font-size: ${fontSizes.sm};
    cursor: pointer;
    text-align: left;
    transition: all 0.15s;

    &:hover {
      border-color: ${theme.colors.primary};
      background: ${theme.colors.primary}08;
    }
  `;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          css={css`
            position: fixed;
            inset: 0;
            background: ${theme.colors.overlay};
            z-index: 50;
          `}
        />
        <Dialog.Content
          css={css`
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: ${theme.colors.surface};
            border: 1px solid ${theme.colors.border};
            border-radius: 12px;
            padding: ${spacing.xl};
            width: 90vw;
            max-width: 380px;
            z-index: 51;
            display: flex;
            flex-direction: column;
            gap: ${spacing.lg};
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <Dialog.Title
              css={css`
                margin: 0;
                font-size: ${fontSizes.xl};
                color: ${theme.colors.text};
                font-weight: 700;
              `}
            >
              Export Speech
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close dialog"
              css={css`
                background: none;
                border: none;
                color: ${theme.colors.textSecondary};
                cursor: pointer;
                font-size: 20px;
                min-width: 44px;
                min-height: 44px;
                display: flex;
                align-items: center;
                justify-content: center;

                &:hover {
                  color: ${theme.colors.text};
                }
              `}
            >
              ×
            </Dialog.Close>
          </div>

          <Dialog.Description
            css={css`
              margin: 0;
              font-size: ${fontSizes.sm};
              color: ${theme.colors.textSecondary};
            `}
          >
            Choose a format to download your speech.
          </Dialog.Description>

          <div
            data-testid="export-summary"
            css={css`
              background: ${theme.colors.surface};
              border: 1px solid ${theme.colors.border};
              border-radius: 8px;
              padding: ${spacing.md};
            `}
          >
            <div
              css={css`
                font-weight: 600;
                color: ${theme.colors.text};
                font-size: ${fontSizes.sm};
                margin-bottom: 4px;
              `}
            >
              &ldquo;{title}&rdquo;
            </div>
            <div
              css={css`
                font-size: ${fontSizes.xs};
                color: ${theme.colors.textSecondary};
              `}
            >
              {sections.length} {sections.length === 1 ? 'section' : 'sections'}
              {' \u00B7 '}
              {sections.reduce((sum, s) => sum + s.wordCount, 0)} words
              {' \u00B7 ~'}
              {formatMs(sections.reduce((sum, s) => sum + (s.estimatedDurationMs ?? 0), 0))}
            </div>
          </div>

          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: ${spacing.sm};
            `}
          >
            <button type="button" onClick={handleTextExport} css={buttonStyle}>
              <FileText size={20} aria-hidden="true" />
              Download as Text (.txt)
            </button>
            <button type="button" onClick={handlePdfExport} css={buttonStyle}>
              <FileDown size={20} aria-hidden="true" />
              Download as PDF (.pdf)
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
